import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TenantLayout from '../components/TenantLayout';
import { useSite } from '../context/SiteContext';
import TicketDownloadModal from '../components/TicketDownloadModal';

const FunctionHallCalendar = () => {
  const { selectedSite, sites } = useSite();
  const [selectedSiteName, setSelectedSiteName] = useState('');

  useEffect(() => {
    if (selectedSite === 'all') {
      setSelectedSiteName('All Sites');
    } else if (selectedSite && sites.length > 0) {
      const site = sites.find(s => s._id === selectedSite);
      setSelectedSiteName(site?.name || 'Selected Site');
    }
  }, [selectedSite, sites]);
  const [functionHalls, setFunctionHalls] = useState([]);
  const [selectedHall, setSelectedHall] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [viewMode, setViewMode] = useState('month');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showBlockDateModal, setShowBlockDateModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedBlockedDate, setSelectedBlockedDate] = useState(null);
  const [showOfflineBookingModal, setShowOfflineBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [offlineBookingForm, setOfflineBookingForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    eventTime: '',
    duration: 4,
    guestCount: '',
    eventType: '',
    specialRequests: '',
    status: 'confirmed',
    selectedServices: {},
    adults: 1,
    children: 0
  });
  const [availableServices, setAvailableServices] = useState({});
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockDateForm, setBlockDateForm] = useState({ startDate: '', endDate: '', reason: '' });
  const [stats, setStats] = useState({ total: 0, confirmed: 0, pending: 0, revenue: 0 });
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictingBookings, setConflictingBookings] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [generatedTicket, setGeneratedTicket] = useState(null);

  useEffect(() => {
    fetchFunctionHalls();
  }, [selectedSite]);

  useEffect(() => {
    if (selectedHall) {
      fetchCalendarData();
      fetchBlockedDates();
      loadAvailableServices();
    }
  }, [selectedHall, currentDate]);

  useEffect(() => {
    if (bookings.length > 0) {
      calculateStats(bookings);
    }
  }, [viewMode, bookings]);

  const loadAvailableServices = () => {
    console.log('Loading available services for hall:', selectedHall?.name);
    console.log('Function hall settings:', selectedHall?.functionHallSettings);
    if (selectedHall?.functionHallSettings?.amenities) {
      console.log('Found amenities:', selectedHall.functionHallSettings.amenities);
      setAvailableServices(selectedHall.functionHallSettings.amenities);
    } else {
      console.log('No amenities found, setting empty object');
      setAvailableServices({});
    }
  };

  const fetchFunctionHalls = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tickets`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      let halls = response.data.tickets.filter(t => 
        t.category === 'function_hall_ticket' || 
        t.name?.toLowerCase().includes('hall') ||
        t.name?.toLowerCase().includes('banquet')
      );
      
      if (selectedSite && selectedSite !== 'all') {
        halls = halls.filter(h => h.siteId === selectedSite);
      }
      
      setFunctionHalls(halls);
      if (halls.length > 0) setSelectedHall(halls[0]);
    } catch (error) {
      console.error('Error fetching function halls:', error);
    }
  };

  const fetchCalendarData = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/availability/calendar/${selectedHall._id}?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setBookings(response.data.bookings);
      calculateStats(response.data.bookings);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const calculateStats = (allBookings) => {
    let filteredBookings = allBookings;
    
    if (viewMode === 'week') {
      const weekDays = getDaysInWeek();
      const weekDates = weekDays.map(day => day.date.toDateString());
      filteredBookings = allBookings.filter(b => 
        weekDates.includes(new Date(b.date).toDateString())
      );
    }
    
    const confirmed = filteredBookings.filter(b => b.status === 'confirmed').length;
    const pending = filteredBookings.filter(b => b.status === 'pending').length;
    const revenue = filteredBookings
      .filter(b => b.status === 'confirmed')
      .reduce((sum, b) => sum + (b.totalAmount || 0), 0);
    
    setStats({
      total: filteredBookings.length,
      confirmed,
      pending,
      revenue
    });
  };

  const fetchBlockedDates = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/blocked-dates/${selectedHall._id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setBlockedDates(response.data.blockedDates);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const handleBlockDate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_BASE_URL}/api/blocked-dates`,
        {
          ticketTypeId: selectedHall._id,
          startDate: blockDateForm.startDate,
          endDate: blockDateForm.endDate || blockDateForm.startDate,
          reason: blockDateForm.reason
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setShowBlockDateModal(false);
      setBlockDateForm({ startDate: '', endDate: '', reason: '' });
      fetchBlockedDates();
    } catch (error) {
      console.error('Error blocking date:', error);
      if (error.response?.status === 409 && error.response?.data?.conflictingBookings) {
        setConflictingBookings(error.response.data.conflictingBookings);
        setShowConflictModal(true);
      } else {
        alert('Error blocking date: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleUnblockDate = async () => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/blocked-dates/${selectedBlockedDate._id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setShowUnblockModal(false);
      setSelectedBlockedDate(null);
      fetchBlockedDates();
    } catch (error) {
      console.error('Error unblocking date:', error);
      alert('Error unblocking date: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleOfflineBooking = async (e) => {
    e.preventDefault();
    try {
      const servicesTotal = Object.entries(offlineBookingForm.selectedServices || {})
        .reduce((sum, [service, isSelected]) => {
          if (isSelected && availableServices && availableServices[service] && availableServices[service].price) {
            return sum + availableServices[service].price;
          }
          return sum;
        }, 0);

      // Convert selected services to proper array format with prices
      const selectedServicesArray = [];
      if (offlineBookingForm.selectedServices) {
        Object.entries(offlineBookingForm.selectedServices).forEach(([serviceName, isSelected]) => {
          if (isSelected && availableServices[serviceName]) {
            selectedServicesArray.push({
              name: serviceName,
              price: availableServices[serviceName].price || 0,
              quantity: 1,
              enabled: true
            });
          }
        });
      }

      const bookingData = {
        ticketTypeId: selectedHall._id,
        bookingDate: selectedDate.getFullYear() + '-' + String(selectedDate.getMonth() + 1).padStart(2, '0') + '-' + String(selectedDate.getDate()).padStart(2, '0'),
        visitDate: selectedDate.getFullYear() + '-' + String(selectedDate.getMonth() + 1).padStart(2, '0') + '-' + String(selectedDate.getDate()).padStart(2, '0'),
        quantity: 1,
        adults: parseInt(offlineBookingForm.adults),
        children: parseInt(offlineBookingForm.children),
        customerDetails: {
          name: offlineBookingForm.customerName,
          phone: offlineBookingForm.customerPhone,
          email: offlineBookingForm.customerEmail || 'noemail@example.com'
        },
        selectedServices: selectedServicesArray,
        categorySpecificData: {
          eventTime: offlineBookingForm.eventTime,
          duration: offlineBookingForm.duration,
          guestCount: parseInt(offlineBookingForm.guestCount),
          eventType: offlineBookingForm.eventType,
          selectedServices: offlineBookingForm.selectedServices
        },
        specialRequirements: offlineBookingForm.specialRequests,
        status: offlineBookingForm.status,
        payment: {
          gateway: 'offline',
          status: 'success',
          paidAt: new Date()
        },
        totalAmount: selectedHall.price + servicesTotal
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/bookings`,
        bookingData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Generate ticket after booking
      if (response.data.booking) {
        const ticketResponse = await generateTicket(response.data.booking._id);
        if (ticketResponse) {
          setGeneratedTicket({
            bookingId: response.data.booking.bookingId,
            customerDetails: {
              name: offlineBookingForm.customerName,
              phone: offlineBookingForm.customerPhone,
              email: offlineBookingForm.customerEmail || 'noemail@example.com'
            },
            ticketTypeId: {
              name: selectedHall.name,
              city: selectedHall.city,
              category: selectedHall.category
            },
            visitDate: selectedDate.toISOString().split('T')[0],
            quantity: 1,
            adults: parseInt(offlineBookingForm.adults),
            children: parseInt(offlineBookingForm.children),
            totalAmount: selectedHall.price + servicesTotal,
            status: offlineBookingForm.status,
            qrCode: ticketResponse.qrCode,
            categorySpecificData: {
              eventTime: offlineBookingForm.eventTime,
              duration: offlineBookingForm.duration,
              guestCount: parseInt(offlineBookingForm.guestCount),
              eventType: offlineBookingForm.eventType,
              selectedServices: selectedServicesArray.length > 0 ? selectedServicesArray : undefined
            },
            createdAt: new Date().toISOString()
          });
          setShowTicketModal(true);
        }
      }
      
      setShowOfflineBookingModal(false);
      fetchCalendarData();
    } catch (error) {
      console.error('Error creating offline booking:', error);
      alert('Error creating booking: ' + (error.response?.data?.message || error.message));
    }
  };

  const generateTicket = async (bookingId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/bookings/${bookingId}/generate-ticket`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating ticket:', error);
      return null;
    }
  };

  const calculateTotal = () => {
    const basePrice = selectedHall?.price || 0;
    const servicesTotal = Object.entries(offlineBookingForm.selectedServices || {})
      .reduce((sum, [service, isSelected]) => {
        if (isSelected && availableServices && availableServices[service] && availableServices[service].price) {
          return sum + availableServices[service].price;
        }
        return sum;
      }, 0);
    return basePrice + servicesTotal;
  };

  const handleServiceToggle = (serviceName) => {
    setOfflineBookingForm({
      ...offlineBookingForm,
      selectedServices: {
        ...offlineBookingForm.selectedServices,
        [serviceName]: !offlineBookingForm.selectedServices?.[serviceName]
      }
    });
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayBookings = bookings.filter(b => 
        new Date(b.date).toDateString() === date.toDateString()
      );
      days.push({ day, date, bookings: dayBookings });
    }
    
    return days;
  };

  const getDaysInWeek = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dayBookings = bookings.filter(b => 
        new Date(b.date).toDateString() === date.toDateString()
      );
      days.push({ day: date.getDate(), date, bookings: dayBookings });
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    if (selectedHall) {
      fetchCalendarData();
    }
  };

  const handleDayClick = (dayData) => {
    if (isDateBlocked(dayData.date)) {
      const blockedDate = blockedDates.find(blocked => {
        const blockStart = new Date(blocked.startDate);
        const blockEnd = new Date(blocked.endDate);
        blockStart.setHours(0, 0, 0, 0);
        blockEnd.setHours(23, 59, 59, 999);
        return dayData.date >= blockStart && dayData.date <= blockEnd;
      });
      setSelectedBlockedDate(blockedDate);
      setShowUnblockModal(true);
    } else if (dayData.bookings.length > 0) {
      setSelectedBooking(dayData.bookings[0]);
      setShowBookingModal(true);
    } else if (!isPastDate(dayData.date)) {
      setSelectedDate(dayData.date);
      setOfflineBookingForm({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        eventTime: '',
        duration: 4,
        guestCount: '',
        eventType: '',
        specialRequests: '',
        status: 'confirmed',
        selectedServices: {},
        adults: 1,
        children: 0
      });
      setShowOfflineBookingModal(true);
    }
  };

  const getBookingStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-500';
      case 'pending': return 'bg-amber-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateBlocked = (date) => {
    return blockedDates.some(blocked => {
      const blockStart = new Date(blocked.startDate);
      const blockEnd = new Date(blocked.endDate);
      blockStart.setHours(0, 0, 0, 0);
      blockEnd.setHours(23, 59, 59, 999);
      return date >= blockStart && date <= blockEnd;
    });
  };

  return (
    <TenantLayout userRole="tenant_admin">
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-green-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-green-700 text-white px-4 py-4 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">📅 Function Hall Calendar</h1>
              <p className="text-sm text-teal-100">Manage bookings and availability</p>
            </div>
            <div className="flex items-center gap-4 relative z-10">
              <button
                type="button"
                onClick={() => {
                  console.log('Today button clicked');
                  goToToday();
                }}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors backdrop-blur-sm cursor-pointer relative z-10"
                style={{ pointerEvents: 'auto' }}
              >
                Today
              </button>
              {selectedHall && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      console.log('Block Dates button clicked');
                      setShowBlockDateModal(true);
                    }}
                    className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white rounded-lg font-medium transition-colors backdrop-blur-sm cursor-pointer relative z-10"
                    style={{ pointerEvents: 'auto' }}
                  >
                    Block Dates
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      console.log('Offline Booking button clicked');
                      setSelectedDate(new Date());
                      setOfflineBookingForm({
                        customerName: '',
                        customerPhone: '',
                        customerEmail: '',
                        eventTime: '',
                        duration: 4,
                        guestCount: '',
                        eventType: '',
                        specialRequests: '',
                        status: 'confirmed',
                        selectedServices: {},
                        adults: 1,
                        children: 0
                      });
                      setShowOfflineBookingModal(true);
                    }}
                    className="px-4 py-2 bg-emerald-500/80 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors backdrop-blur-sm cursor-pointer relative z-10"
                    style={{ pointerEvents: 'auto' }}
                  >
                    📝 Offline Booking
                  </button>
                </>
              )}
              <div className="flex bg-white/20 backdrop-blur-sm rounded-lg p-1 relative z-10" style={{ pointerEvents: 'auto' }}>
                <button
                  type="button"
                  onClick={() => {
                    console.log('Month button clicked, current viewMode:', viewMode);
                    setViewMode('month');
                  }}
                  className={`px-4 py-2 rounded-md font-medium transition-colors cursor-pointer relative z-10 ${
                    viewMode === 'month' ? 'bg-white text-teal-700 shadow-sm' : 'text-white hover:text-teal-100'
                  }`}
                  style={{ pointerEvents: 'auto' }}
                >
                  Month
                </button>
                <button
                  type="button"
                  onClick={() => {
                    console.log('Week button clicked, current viewMode:', viewMode);
                    setViewMode('week');
                  }}
                  className={`px-4 py-2 rounded-md font-medium transition-colors cursor-pointer relative z-10 ${
                    viewMode === 'week' ? 'bg-white text-teal-700 shadow-sm' : 'text-white hover:text-teal-100'
                  }`}
                  style={{ pointerEvents: 'auto' }}
                >
                  Week
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-3">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 -mt-2">
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-lg p-3 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-teal-100">Total</p>
                  <p className="text-xl font-bold">{stats.total}</p>
                </div>
                <span className="text-lg">📊</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-lg p-3 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-emerald-100">Confirmed</p>
                  <p className="text-xl font-bold">{stats.confirmed}</p>
                </div>
                <span className="text-lg">✅</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white rounded-lg p-3 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-cyan-100">Pending</p>
                  <p className="text-xl font-bold">{stats.pending}</p>
                </div>
                <span className="text-lg">⏳</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-3 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-green-100">Revenue</p>
                  <p className="text-xl font-bold">₹{stats.revenue.toLocaleString()}</p>
                </div>
                <span className="text-lg">💰</span>
              </div>
            </div>
          </div>

          {/* Side by Side Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left Side - Controls */}
            <div className="lg:col-span-1 space-y-4">
              {/* Hall Selection */}
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-white/20 shadow-lg">
                <div className="mb-3">
                  <h2 className="text-lg font-bold text-gray-900">
                    {selectedSite === 'all' ? 'Select Function Hall' : 'Function Halls'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {selectedSite === 'all' 
                      ? 'Choose a hall to view calendar' 
                      : `Available at selected site`
                    }
                  </p>
                </div>
                
                {functionHalls.length > 0 ? (
                  <div className="space-y-2">
                    {functionHalls.map(hall => (
                      <button
                        key={hall._id}
                        onClick={() => setSelectedHall(hall)}
                        className={`w-full p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                          selectedHall?._id === hall._id
                            ? 'border-teal-500 bg-teal-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            selectedHall?._id === hall._id ? 'bg-teal-500' : 'bg-gray-300'
                          }`}></div>
                          <div>
                            <h3 className="font-semibold text-sm text-gray-900">{hall.name}</h3>
                            <p className="text-xs text-gray-600">₹{hall.price}/day</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="text-4xl mb-3">🏛️</div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      {selectedSite === 'all' ? 'No Function Halls Found' : 'No Function Halls at This Site'}
                    </h3>
                    <p className="text-xs text-gray-600 mb-3">
                      {selectedSite === 'all' 
                        ? 'No function hall tickets found.'
                        : `This site doesn't have any function hall tickets assigned.`
                      }
                    </p>
                    <button
                      onClick={() => window.location.href = '/tenant-dashboard/tickets'}
                      className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg inline-flex items-center gap-2"
                    >
                      <span>+</span>
                      Add Tickets
                    </button>
                  </div>
                )}
              </div>

              {/* Legend */}
              {selectedHall && (
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-white/20 shadow-lg">
                  <h3 className="text-sm font-bold text-gray-900 mb-3">Legend</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-emerald-500 rounded"></div>
                      <span className="text-xs text-gray-700">Confirmed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-amber-500 rounded"></div>
                      <span className="text-xs text-gray-700">Pending</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded"></div>
                      <span className="text-xs text-gray-700">Cancelled</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 border-2 border-teal-500 rounded"></div>
                      <span className="text-xs text-gray-700">Today</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded"></div>
                      <span className="text-xs text-gray-700">Blocked</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Calendar */}
            <div className="lg:col-span-2">
              {selectedHall ? (
                <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-white/20 shadow-lg overflow-hidden">
                  {/* Calendar Header */}
                  <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-3">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => navigateMonth(-1)}
                        className="p-1 hover:bg-white/20 rounded transition-colors"
                      >
                        <span className="text-lg">←</span>
                      </button>
                      
                      <div className="text-center">
                        <h3 className="text-lg font-bold">
                          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h3>
                        <p className="text-xs text-teal-100">
                          {selectedHall.name}
                          {selectedSite !== 'all' && selectedSiteName && (
                            <span className="text-teal-200"> • {selectedSiteName}</span>
                          )}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => navigateMonth(1)}
                        className="p-1 hover:bg-white/20 rounded transition-colors"
                      >
                        <span className="text-lg">→</span>
                      </button>
                    </div>
                  </div>

                  {/* Calendar Grid */}
                  <div className="p-3" key={`${currentDate.getFullYear()}-${currentDate.getMonth()}-${viewMode}`}>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="p-2 text-center text-xs font-semibold text-gray-700 bg-gray-50 rounded">
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className={`grid gap-1 ${
                      viewMode === 'month' ? 'grid-cols-7' : 'grid-cols-7'
                    }`}>
                      {(viewMode === 'month' ? getDaysInMonth() : getDaysInWeek()).map((dayData, index) => (
                        <div
                          key={index}
                          onClick={() => dayData && handleDayClick(dayData)}
                          className={`min-h-16 p-1 rounded border-2 transition-all duration-200 cursor-pointer ${
                            !dayData
                              ? 'border-transparent'
                              : isDateBlocked(dayData.date)
                              ? 'border-red-400 bg-red-100'
                              : isToday(dayData.date)
                              ? 'border-teal-500 bg-teal-50'
                              : isPastDate(dayData.date)
                              ? 'border-gray-200 bg-gray-50'
                              : dayData.bookings.length > 0
                              ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          {dayData && (
                            <>
                              <div className={`text-xs font-semibold mb-1 ${
                                isToday(dayData.date) ? 'text-teal-600' : isDateBlocked(dayData.date) ? 'text-red-600' : 'text-gray-900'
                              }`}>
                                {dayData.day}
                              </div>
                              
                              {isDateBlocked(dayData.date) && (
                                <div className="text-xs px-1 py-0.5 rounded bg-red-500 text-white mb-1">
                                  Block
                                </div>
                              )}
                              
                              {dayData.bookings.slice(0, 2).map((booking, idx) => (
                                <div
                                  key={idx}
                                  className={`text-xs px-1 py-0.5 rounded text-white mb-1 ${
                                    getBookingStatusColor(booking.status)
                                  }`}
                                >
                                  {booking.customerName?.split(' ')[0] || 'Book'}
                                </div>
                              ))}
                              
                              {dayData.bookings.length > 2 && (
                                <div className="text-xs text-gray-500 font-medium">
                                  +{dayData.bookings.length - 2}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-8 border border-white/20 shadow-lg text-center">
                  <div className="text-6xl mb-4">📅</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Select a Function Hall</h3>
                  <p className="text-gray-600">Choose a function hall from the left panel to view its booking calendar</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Booking Details Modal */}
        {showBookingModal && selectedBooking && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🎫</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Booking Details</h3>
                    <p className="text-sm text-gray-600">Function Hall Reservation</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-xl">×</span>
                </button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Booking ID</label>
                    <p className="text-sm font-mono font-semibold text-gray-900">{selectedBooking.bookingId || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</label>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium text-white mt-1 ${
                      getBookingStatusColor(selectedBooking.status)
                    }`}>
                      {selectedBooking.status.charAt(0).toUpperCase() + selectedBooking.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Customer Name</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedBooking.customerName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Event Date</label>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(selectedBooking.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                {selectedBooking.eventTime && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Event Time</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedBooking.eventTime}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Duration</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedBooking.duration || 'N/A'} hours</p>
                    </div>
                  </div>
                )}
                
                {selectedBooking.guestCount && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Guest Count</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedBooking.guestCount}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Event Type</label>
                      <p className="text-lg font-semibold text-gray-900 capitalize">{selectedBooking.eventType || 'N/A'}</p>
                    </div>
                  </div>
                )}
                
                {selectedBooking.totalAmount && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <label className="text-sm font-medium text-green-700">Total Amount</label>
                      <p className="text-xl font-bold text-green-600">₹{selectedBooking.totalAmount.toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={async () => {
                    try {
                      // Fetch full booking details
                      const response = await axios.get(
                        `${API_BASE_URL}/api/bookings/${selectedBooking.bookingId}`,
                        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                      );
                      
                      if (response.data.booking) {
                        setGeneratedTicket(response.data.booking);
                        setShowTicketModal(true);
                        setShowBookingModal(false);
                      }
                    } catch (error) {
                      console.error('Error fetching booking details:', error);
                      alert('Error loading ticket details');
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white rounded-lg font-medium transition-all shadow-lg inline-flex items-center justify-center gap-2"
                >
                  💾 Download Ticket
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Block Date Modal */}
        {showBlockDateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Block Dates</h3>
                <button
                  onClick={() => setShowBlockDateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
              
              <form onSubmit={handleBlockDate}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={blockDateForm.startDate}
                      onChange={(e) => setBlockDateForm({...blockDateForm, startDate: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
                    <input
                      type="date"
                      value={blockDateForm.endDate}
                      onChange={(e) => setBlockDateForm({...blockDateForm, endDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <textarea
                      value={blockDateForm.reason}
                      onChange={(e) => setBlockDateForm({...blockDateForm, reason: e.target.value})}
                      required
                      rows={3}
                      placeholder="e.g., Maintenance, Private event, Holiday"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowBlockDateModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Block Dates
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Unblock Date Modal */}
        {showUnblockModal && selectedBlockedDate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Blocked Date Details</h3>
                <button
                  onClick={() => setShowUnblockModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Date Range</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(selectedBlockedDate.startDate).toLocaleDateString()}
                    {selectedBlockedDate.endDate && new Date(selectedBlockedDate.startDate).toDateString() !== new Date(selectedBlockedDate.endDate).toDateString() && 
                      ` - ${new Date(selectedBlockedDate.endDate).toLocaleDateString()}`
                    }
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Reason</label>
                  <p className="text-lg font-semibold text-gray-900">{selectedBlockedDate.reason}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Blocked On</label>
                  <p className="text-sm text-gray-700">
                    {new Date(selectedBlockedDate.createdAt).toLocaleDateString()} at {new Date(selectedBlockedDate.createdAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowUnblockModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleUnblockDate}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  Unblock Dates
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Booking Conflict Modal */}
        {showConflictModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-red-600">⚠️ Booking Conflicts Found</h3>
                <button
                  onClick={() => setShowConflictModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-gray-700 mb-4">
                  Cannot block the selected dates because there are existing bookings. Please cancel or reschedule these bookings first:
                </p>
                
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {conflictingBookings.map((booking, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{booking.customerName}</p>
                          <p className="text-sm text-gray-600">Date: {booking.date}</p>
                          <p className="text-sm text-gray-600">Quantity: {booking.quantity}</p>
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {booking.bookingId.slice(-6)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-800 mb-2">💡 Suggestions:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Contact customers to reschedule their bookings</li>
                  <li>• Cancel bookings and process refunds if needed</li>
                  <li>• Choose different dates that don't have bookings</li>
                  <li>• Block individual dates instead of a range</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConflictModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowConflictModal(false);
                    window.location.href = '/tenant-dashboard/bookings';
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Manage Bookings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Offline Booking Modal */}
        {showOfflineBookingModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">📝 Offline Booking</h3>
                <button
                  onClick={() => setShowOfflineBookingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 font-medium">
                  {selectedHall?.name} - {selectedDate?.toLocaleDateString()}
                </p>
              </div>
              
              <form onSubmit={handleOfflineBooking}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                    <input
                      type="text"
                      value={offlineBookingForm.customerName}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, customerName: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={offlineBookingForm.customerPhone}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, customerPhone: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={offlineBookingForm.customerEmail}
                    onChange={(e) => setOfflineBookingForm({...offlineBookingForm, customerEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Event Time</label>
                    <input
                      type="time"
                      value={offlineBookingForm.eventTime}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, eventTime: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (hours)</label>
                    <input
                      type="number"
                      value={offlineBookingForm.duration}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, duration: parseInt(e.target.value)})}
                      min="1"
                      max="12"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guest Count</label>
                    <input
                      type="number"
                      value={offlineBookingForm.guestCount}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, guestCount: e.target.value})}
                      min="1"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adults</label>
                    <input
                      type="number"
                      value={offlineBookingForm.adults}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, adults: e.target.value})}
                      min="1"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Children</label>
                    <input
                      type="number"
                      value={offlineBookingForm.children}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, children: e.target.value})}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                    <select
                      value={offlineBookingForm.eventType}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, eventType: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Event Type</option>
                      <option value="wedding">Wedding</option>
                      <option value="birthday">Birthday Party</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="religious">Religious Ceremony</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={offlineBookingForm.status}
                      onChange={(e) => setOfflineBookingForm({...offlineBookingForm, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="confirmed">✅ Confirmed</option>
                      <option value="pending">⏳ Pending</option>
                    </select>
                  </div>
                </div>
                
                {availableServices && Object.keys(availableServices).length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Services</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(availableServices)
                        .filter(([serviceName, serviceData]) => serviceData && typeof serviceData === 'object' && serviceData.enabled)
                        .map(([serviceName, serviceData]) => {
                          const isSelected = offlineBookingForm.selectedServices?.[serviceName] || false;
                          return (
                            <label key={serviceName} className={`flex items-center gap-3 p-3 border-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-all ${
                              isSelected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
                            }`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleServiceToggle(serviceName)}
                                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 capitalize text-sm">{serviceName.replace(/_/g, ' ')}</div>
                                <div className="text-xs text-gray-600">₹{serviceData.price || 0}</div>
                              </div>
                            </label>
                          );
                        })
                      }
                    </div>
                  </div>
                )}
                
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Total Amount:</span>
                    <span className="text-xl font-bold text-green-600">₹{calculateTotal()}</span>
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
                  <textarea
                    value={offlineBookingForm.specialRequests}
                    onChange={(e) => setOfflineBookingForm({...offlineBookingForm, specialRequests: e.target.value})}
                    rows={3}
                    placeholder="Any special requirements or notes..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowOfflineBookingModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    📝 Create Booking
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Ticket Download Modal */}
        {showTicketModal && generatedTicket && (
          <TicketDownloadModal
            isOpen={showTicketModal}
            mode="display"
            booking={generatedTicket}
            onClose={() => setShowTicketModal(false)}
          />
        )}
      </div>
    </TenantLayout>
  );
};

export default FunctionHallCalendar;